import { DateTime } from 'luxon';

export type EmphasisSegment = { text: string; bold: boolean };

/**
 * Splits disclosure copy on `**` markers into plain and bold segments. The legal text needs bold
 * headings, bold labels and bold runs mid-sentence, so the markers travel with the copy rather than
 * the structure being modelled separately.
 */
export const parseEmphasis = (text: string): EmphasisSegment[] =>
    text
        .split('**')
        .map((segment, index) => ({ text: segment, bold: index % 2 === 1 }))
        .filter((segment) => segment.text !== '');

export const DEFAULT_SENT_AT_TIME_ZONE = 'America/New_York';
export const DEFAULT_SENT_AT_ZONE_LABEL = 'ET';

/**
 * Formats the consent banner's "Sent" stamp, e.g. `Aug 24, 2026 at 11:42 AM ET`.
 *
 * The zone label is passed in rather than derived, because luxon renders the offset name
 * ("EDT"/"EST") whereas the design shows the zone ("ET"). Returns an empty string for a missing or
 * unparseable timestamp so the caller can simply omit the stamp.
 */
export const formatSentAt = (
    isoTimestamp: string | undefined,
    timeZone: string = DEFAULT_SENT_AT_TIME_ZONE,
    zoneLabel: string = DEFAULT_SENT_AT_ZONE_LABEL
): string => {
    if (!isoTimestamp) return '';

    const parsed = DateTime.fromISO(isoTimestamp, { zone: timeZone });
    if (!parsed.isValid) return '';

    return `${parsed.toFormat("LLL d, yyyy 'at' h:mm a")} ${zoneLabel}`;
};
